import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { ChevronDown } from 'lucide-react';
import { cn } from 'ui/lib/utils';
import { getCategories, CategoryDocument } from '@util/firestore/categories';
import { getCategoryMega } from '@util/firestore/algolia/search.service';
import { getProductUrl } from '@util/urlHelpers';
import type { ProductDocument } from '@util/types/firestore/products';
import MegaMenu, { type MegaMenuData } from './MegaMenu';
import { gfListingsIndex } from './algoliaIndex';

interface CategoryNavItem {
  label: string;
  href: string;
  /** Level-1 category slug — presence gives the item a Firestore-backed mega menu. */
  slug?: string;
  align?: 'left' | 'right';
  hot?: boolean;
  deal?: boolean;
}

// Order + labels match gearfocus-v2; hrefs use gearfocus-web's real routes.
// The six items with a `slug` render a mega menu populated from Firestore + Algolia.
// Exported so other zero-result/fallback UI (e.g. SearchEmptyState, #1401) can
// reuse the same top-level category list instead of duplicating it.
export const defaultItems: CategoryNavItem[] = [
  { label: 'New Arrivals', href: '/collections/new-arrivals', hot: true },
  { label: 'Cameras', href: '/c/digital-cameras', slug: 'digital-cameras' },
  { label: 'Lenses', href: '/c/photo-video-lenses', slug: 'photo-video-lenses' },
  { label: 'Video', href: '/c/video-camcorders', slug: 'video-camcorders' },
  { label: 'Lighting', href: '/c/lighting-studio', slug: 'lighting-studio' },
  { label: 'Audio', href: '/c/professional-audio', slug: 'professional-audio' },
  { label: 'Drones', href: '/c/drones-aerial-imaging', slug: 'drones-aerial-imaging' },
  { label: 'Vintage', href: '/c/vintage-camera-equipment' },
  { label: 'Computers', href: '/c/computers-electronics' },
  { label: 'Accessories', href: '/c/photo-video-accessories' },
];

// Right-justified group. Brands/Start Selling/Help moved up to the header's
// TopBar utility links (Bill, 2026-08-21) — Deals is the sole survivor here,
// pinned as the right-most item in the category bar.
const rightItems: CategoryNavItem[] = [
  { label: 'Deals', href: '/collections/deals-and-steals', deal: true },
];

const ALGOLIA_INDEX = gfListingsIndex();

// How long the pointer must rest on a category before its mega-menu data is
// fetched. Long enough that sweeping the nav costs nothing, short enough that a
// deliberate hover still feels instant.
const HOVER_INTENT_MS = 150;

type SubLink = { label: string; slug: string; href: string };
type MegaExtra = {
  total: number;
  counts: Record<string, number>;
  brands: { name: string; count: number }[];
  sample: ProductDocument[];
  feature: ProductDocument | null;
};

// Scope the brand to the parent category so results match the shown count
// (the /search page filters on ?category=<name>&brand=<name>).
const brandHref = (categoryName: string, brand: string) =>
  `/search?view=list&category=${encodeURIComponent(categoryName)}&brand=${encodeURIComponent(brand)}`;

/**
 * Position the (fixed-width) mega panel under its trigger, in VIEWPORT
 * coordinates.
 *
 * The panel is `position: fixed`, not `absolute`, and that is load-bearing: the
 * row of triggers lives in an `overflow-x-auto` scroll container, and per spec
 * an `overflow-x` other than `visible` forces `overflow-y` to compute to `auto`
 * as well. That made the 40px-tall nav clip the ~362px panel to nothing — it
 * reached the open state on hover and painted zero pixels. An absolutely
 * positioned panel can't escape, because its containing block (the `li`) is
 * inside the clipper; a fixed one is laid out against the viewport and is not
 * clipped by ancestor overflow. Keeping the panel a DOM child of the `li`
 * preserves tab order and the group-hover/group-focus-within reveal.
 *
 * Do not introduce `transform`, `filter`, `perspective`, `will-change` or
 * `contain` on any ancestor of this nav: each of those makes the ancestor a
 * containing block for fixed descendants and re-creates the clip.
 */
function positionPanel(li: HTMLElement) {
  const panel = li.querySelector<HTMLElement>('[data-mega-panel]');
  if (!panel) return;
  const margin = 12;
  const width = panel.offsetWidth;
  const rect = li.getBoundingClientRect();
  const vw = document.documentElement.clientWidth;
  // Anchored under the trigger, pulled left only as far as needed to clear the
  // viewport's right edge, and never past the left margin.
  let left = Math.min(rect.left, vw - margin - width);
  if (left < margin) left = margin;
  panel.style.left = `${left}px`;
  panel.style.top = `${rect.bottom}px`;
}

interface CategoryNavProps {
  items?: CategoryNavItem[];
  className?: string;
}

/**
 * Ported from gearfocus-v2's marketplace CategoryNav. On first hover a single
 * batched Algolia request loads: total + per-sub-category counts (By Type),
 * a listing sample (used to derive By Brand + Popular Now), and a featured/deal
 * listing. Reveal animates via a CSS group-hover transition.
 */
const CategoryNav = ({ items = defaultItems, className }: CategoryNavProps) => {
  const [subsBySlug, setSubsBySlug] = useState<Record<string, SubLink[]>>({});
  const [nameBySlug, setNameBySlug] = useState<Record<string, string>>({});
  const [megaBySlug, setMegaBySlug] = useState<Record<string, MegaExtra>>({});
  const requested = useRef<Set<string>>(new Set());
  const navRef = useRef<HTMLElement>(null);

  // A fixed panel is positioned against the viewport, so anything that moves the
  // trigger without moving the panel detaches the two — notably the header's
  // collapse-on-scroll (it shortens by ~54px over 300ms) and window resizes.
  // Re-anchor whichever trigger is currently open; when none is, this is a
  // couple of failed selector matches and costs nothing.
  useEffect(() => {
    const reposition = () => {
      const open = navRef.current?.querySelector<HTMLElement>(
        '[data-mega-item]:hover, [data-mega-item]:focus-within'
      );
      if (open) positionPanel(open);
    };
    window.addEventListener('scroll', reposition, { passive: true });
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition);
      window.removeEventListener('resize', reposition);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getCategories(2)
      .then((all: CategoryDocument[]) => {
        if (cancelled) return;
        const subMap: Record<string, SubLink[]> = {};
        const nameMap: Record<string, string> = {};
        for (const item of items) {
          if (!item.slug) continue;
          const l1 = all.find((c) => c.slug === item.slug && c.level === 1);
          if (!l1) continue;
          nameMap[item.slug] = l1.name;
          subMap[item.slug] = all
            .filter((c) => c.parents?.includes(l1.id))
            .map((c) => ({
              label: c.name,
              slug: c.slug,
              href: `/c/${item.slug}/${c.slug}`,
            }));
        }
        setSubsBySlug(subMap);
        setNameBySlug(nameMap);
      })
      .catch((err) => console.error('CategoryNav: failed to load categories', err));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hover INTENT, not hover. getCategoryMega batches one Algolia query per
  // sub-category plus one for the parent, so a single mouse sweep across the
  // nav used to bill ~89 queries for menus nobody looked at. Nothing is
  // requested until the pointer has rested on one item for HOVER_INTENT_MS.
  //
  // The panel itself still opens instantly (CSS :hover) and positions on
  // mouseenter — only the fetch waits, so this costs no perceived latency on a
  // deliberate hover.
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelHoverIntent = () => {
    if (hoverTimer.current !== null) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  };

  const scheduleMega = (slug: string) => {
    cancelHoverIntent();
    // Already cached — no timer, no query, no reason to wait.
    if (requested.current.has(slug)) return;
    hoverTimer.current = setTimeout(() => {
      hoverTimer.current = null;
      loadMega(slug);
    }, HOVER_INTENT_MS);
  };

  // A pending timer must not outlive the component.
  useEffect(() => cancelHoverIntent, []);

  // Lazily load counts + sample + featured listing the first time a category is
  // hovered. The GF `categories` field stores names, so query by name and key
  // counts back by sub-category slug. `requested` is the per-category cache:
  // added before the fetch so concurrent hovers single-flight, and removed only
  // on failure so a transient error can be retried.
  const loadMega = (slug: string) => {
    if (requested.current.has(slug)) return;
    const categoryName = nameBySlug[slug];
    if (!categoryName) return; // categories not loaded yet; retry on next hover
    requested.current.add(slug);
    const subs = (subsBySlug[slug] ?? []).map((s) => ({ key: s.slug, name: s.label }));
    getCategoryMega(ALGOLIA_INDEX, categoryName, subs, 50)
      .then(({ total, counts, brands, sample }) => {
        const deals = sample.filter((l) => l.on_sale && l.og_price > l.price);
        const pool = deals.length ? deals : sample;
        const feature = pool.length
          ? pool[Math.floor(Math.random() * pool.length)]
          : null;
        setMegaBySlug((prev) => ({
          ...prev,
          [slug]: { total, counts, brands, sample, feature },
        }));
      })
      .catch((err) => {
        requested.current.delete(slug);
        console.error('CategoryNav: failed to load category mega', err);
      });
  };

  const buildMega = (item: CategoryNavItem): MegaMenuData => {
    const slug = item.slug as string;
    const subs = subsBySlug[slug] ?? [];
    const extra = megaBySlug[slug];
    const loaded = Boolean(extra);
    const categoryName = nameBySlug[slug] ?? '';

    // Always render the same three columns + feature slot so the menu keeps a
    // stable width/height; pending sections show skeletons until data streams in.
    const columns: MegaMenuData['columns'] = [
      {
        title: 'By Type',
        // Tied to `loaded` (not just subs being fetched) — the list below is
        // ordered by count, which only exists once `extra` arrives. Skeleton
        // through that window instead of rendering unsorted labels and then
        // reflowing into sorted order once counts land.
        loading: !loaded,
        rows: 6,
        links: subs
          .map((s) => ({
            label: s.label,
            href: s.href,
            count: extra?.counts[s.slug],
          }))
          // Highest-count subcategories first, and — since this is also a
          // slice(0, 6) — this is what decides which subcategories are even
          // shown when there are more than 6: by size, not list position.
          .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
          .slice(0, 6),
      },
      {
        title: 'By Brand',
        loading: !loaded,
        rows: 6,
        links: (extra?.brands ?? []).map((b) => ({
          label: b.name,
          href: brandHref(categoryName, b.name),
          count: b.count,
        })),
      },
    ];

    let feature: MegaMenuData['feature'];
    const listing = extra?.feature;
    if (listing) {
      const img = listing.thumbnail || listing.images?.[0]?.thumb;
      const isDeal = listing.on_sale && listing.og_price > listing.price;
      feature = {
        label: isDeal ? 'Deal of the day' : 'Featured listing',
        title: listing.title,
        price: listing.price,
        originalPrice: isDeal ? listing.og_price : undefined,
        href: getProductUrl(listing.slug),
        image: img ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={img}
            alt={listing.title}
            className="h-full w-full object-contain"
            loading="lazy"
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-gray-100 to-gray-200" />
        ),
      };
    }

    return { columns, align: item.align, feature, featureLoading: !loaded };
  };

  const dot = (
    <span
      aria-hidden="true"
      className="mr-[8px] inline-block size-[6px] shrink-0 animate-pulse rounded-full bg-primary"
    />
  );

  return (
    <nav
      ref={navRef}
      data-slot="category-nav"
      className={cn(
        // Show from lg (was xl-only), so 1024–1279px laptops get a category bar
        // instead of nothing. The row scrolls horizontally rather than wrapping
        // or pushing the page wide when the twelve items don't all fit.
        'relative hidden h-[40px] w-full border-b border-border bg-white lg:flex',
        className
      )}
    >
      <div className="mx-auto flex w-full max-w-default items-center px-4 lg:px-[40px] xl:px-default-padding-x">
        <ul
          className="flex min-w-0 items-center overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{ listStyle: 'none', margin: 0, paddingInlineStart: 0 }}
        >
          {items.map((item) =>
            item.slug ? (
              <li
                key={item.label}
                data-mega-item
                className="group/cat relative shrink-0"
                onMouseEnter={(e) => {
                  scheduleMega(item.slug as string);
                  positionPanel(e.currentTarget);
                }}
                onMouseLeave={cancelHoverIntent}
                // Keyboard focus is deliberate by definition — there is no
                // sweep to debounce, so load immediately.
                onFocus={(e) => {
                  cancelHoverIntent();
                  loadMega(item.slug as string);
                  positionPanel(e.currentTarget);
                }}
              >
                <Link
                  href={item.href}
                  className="inline-flex h-[40px] items-center whitespace-nowrap px-[8px] text-[14px] font-medium text-gray-700 transition-colors hover:text-primary group-hover/cat:text-primary"
                >
                  {item.hot && dot}
                  {item.label}
                  <ChevronDown
                    size={14}
                    aria-hidden="true"
                    className="ml-[4px] transition-transform group-hover/cat:rotate-180 group-focus-within/cat:rotate-180"
                  />
                </Link>
                <div
                  data-mega-panel
                  // Keyboard-openable, not just hover: `group-focus-within`
                  // reveals the panel when the trigger (or anything inside it)
                  // is focused. `invisible` when closed removes its ~50 links
                  // from the tab order entirely — previously they were only
                  // `opacity-0`, so keyboard users tabbed into invisible content.
                  className="invisible pointer-events-none fixed z-[100] origin-top -translate-y-[8px] scale-[0.97] opacity-0 transition-[opacity,transform,visibility] duration-200 ease-out group-hover/cat:visible group-hover/cat:pointer-events-auto group-hover/cat:translate-y-0 group-hover/cat:scale-100 group-hover/cat:opacity-100 group-focus-within/cat:visible group-focus-within/cat:pointer-events-auto group-focus-within/cat:translate-y-0 group-focus-within/cat:scale-100 group-focus-within/cat:opacity-100"
                >
                  <div className="rounded-[10px] border border-gray-200 shadow-xl">
                    <MegaMenu {...buildMega(item)} />
                  </div>
                </div>
              </li>
            ) : (
              <li key={item.label} className="shrink-0">
                <Link
                  href={item.href}
                  className={cn(
                    'inline-flex h-[40px] items-center whitespace-nowrap px-[8px] text-[14px] font-medium transition-colors',
                    item.deal || item.hot
                      ? 'font-semibold text-error hover:text-error'
                      : 'text-gray-700 hover:text-primary'
                  )}
                >
                  {item.label}
                </Link>
              </li>
            )
          )}
        </ul>
        <ul
          className="ml-auto flex shrink-0 items-center"
          style={{ listStyle: 'none', margin: 0, paddingInlineStart: 0 }}
        >
          {rightItems.map((item) => (
            <li key={item.label} className="shrink-0">
              <Link
                href={item.href}
                className={cn(
                  'inline-flex h-[40px] items-center whitespace-nowrap px-[8px] text-[14px] font-medium transition-colors',
                  item.deal
                    ? 'font-semibold text-error hover:text-error'
                    : 'text-gray-700 hover:text-primary'
                )}
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

export default CategoryNav;
export type { CategoryNavItem };
